import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { SummaryTestState, SummaryMetadata, Document, AggregationOptions } from './types';
import { SummaryUpdateStatus } from '../../socket/subscriptions/summaryUpdateHandler';
import { getCompaniesWithDocuments } from '../../api/companyApi';
import { getDocumentSections } from '../../api/summaryTestApi';
import { AIModel } from '../../types/AIModels';
import { SummaryType } from '../research/types';

// Helper function to calculate total characters for selected sections with max chars per section
const calculateSectionCharacters = (
  documents: Document[],
  selectedDocuments: { [documentId: string]: { selectedSectionIdx: number[] } },
  maxCharsPerSection: number | null,
): number => {
  let total = 0;
  for (const doc of documents) {
    const docState = selectedDocuments[doc.id];
    if (!docState) continue;
    for (const idx of docState.selectedSectionIdx) {
      const section = doc.sections[idx];
      if (!section) continue;
      const sectionChars = maxCharsPerSection
        ? Math.min(section.characterCount, maxCharsPerSection)
        : section.characterCount;
      total += sectionChars;
    }
  }
  return total;
};

// Async thunk to set company and fetch documents
export const setCompanyAsync = createAsyncThunk(
  'summaryTest/setCompanyAsync',
  async (companyId: string) => {
    const companiesWithDocs = await getCompaniesWithDocuments([companyId]);
    if (companiesWithDocs.length === 0) {
      throw new Error('No documents found for this company.');
    }

    const companyWithDocs = companiesWithDocs[0];
    const documents = companyWithDocs.documents || [];

    // Sort documents by published_at date (newest first) and ensure all required fields
    const sortedDocuments = documents
      .sort((a, b) => {
        const dateA = new Date(a.published_at || '').getTime();
        const dateB = new Date(b.published_at || '').getTime();
        return dateB - dateA;
      })
      .map((doc) => ({
        id: doc.id,
        name: doc.name,
        type: doc.type,
        published_at: doc.published_at || null,
        fiscal_year: doc.fiscal_year,
        fiscal_quarter: doc.fiscal_quarter,
        sections: [], // Initialize with empty sections array
      }));

    return {
      companyId,
      companyName: companyWithDocs.name,
      documents: sortedDocuments,
    };
  },
);

// Async thunk to set selected document IDs and fetch their sections
export const setSelectedDocumentIdsAsync = createAsyncThunk(
  'summaryTest/setSelectedDocumentIdsAsync',
  async ({
    documentIds,
    currentDocuments,
    currentSelectedSectionIdxMap,
  }: {
    documentIds: string[];
    currentDocuments: Document[];
    currentSelectedSectionIdxMap: { [documentId: string]: number[] };
  }) => {
    const newDocumentIds = documentIds;
    const oldDocumentIds = currentDocuments
      .filter((doc) => doc.sections.length > 0)
      .map((doc) => doc.id);

    // Find documents that are being added (new ones)
    const addedDocumentIds = newDocumentIds.filter((id) => !oldDocumentIds.includes(id));

    // Find documents that are being removed
    const removedDocumentIds = oldDocumentIds.filter((id) => !newDocumentIds.includes(id));

    // Keep section indices for documents that remain selected
    const sectionIdxMapToKeep: { [documentId: string]: number[] } = {};
    for (const docId of Object.keys(currentSelectedSectionIdxMap)) {
      if (!removedDocumentIds.includes(docId)) {
        sectionIdxMapToKeep[docId] = currentSelectedSectionIdxMap[docId];
      }
    }

    // Fetch sections for newly added documents
    const sectionsPromises = addedDocumentIds.map(async (docId) => {
      const sectionsResp = await getDocumentSections(docId);
      return {
        documentId: docId,
        sections: sectionsResp.sections.map((section, index) => ({
          idx: section.idx,
          header: section.header,
          characterCount: section.characterCount,
          index: index,
        })),
        hasEmbeddings: sectionsResp.hasEmbeddings,
      };
    });

    const newDocumentSections = await Promise.all(sectionsPromises);

    return {
      documentIds: newDocumentIds,
      sectionIdxMapToKeep,
      newDocumentSections,
    };
  },
);

const initialState: SummaryTestState = {
  // Company selection
  companyId: null,
  companyName: null,

  // Document and section selection
  documents: [],
  selectedDocuments: {},
  sectionCharacters: 0,
  maxCharsPerSection: null,

  // Prompt
  prompt: '',
  summaryType: 'custom',
  promptName: undefined,

  // Aggregated summary
  aggregationOptions: null,

  // Model selection
  model: null,

  // Summary generation
  summaryText: null,
  summaryMetadata: null,
  summaryStatus: null,
  summaryError: null,
  summaryGeneratedAt: null,

  // Loading states
  isLoadingDocuments: false,
  isLoadingSections: false,
};

export const summaryTestSlice = createSlice({
  name: 'summaryTest',
  initialState,
  reducers: {
    setSummaryType: (state, action: PayloadAction<'custom' | 'predefined'>) => {
      state.summaryType = action.payload;
      // Reset subsequent fields
      state.selectedDocuments = {};
      state.sectionCharacters = 0;
      state.maxCharsPerSection = null;
      state.prompt = '';
      state.promptName = undefined;
      state.model = null;
      state.summaryText = null;
      state.summaryMetadata = null;
      state.summaryStatus = null;
      state.summaryError = null;
      state.summaryGeneratedAt = null;
      state.aggregationOptions = null;
    },
    setPromptName: (state, action: PayloadAction<SummaryType | undefined>) => {
      state.promptName = action.payload;
    },
    // Section actions - combined action that sets both IDs and calculates characters
    setSelectedSectionsForDocument: (
      state,
      action: PayloadAction<{ documentId: string; sectionIdx: number[] }>,
    ) => {
      const { documentId, sectionIdx } = action.payload;
      if (!state.selectedDocuments[documentId]) {
        state.selectedDocuments[documentId] = { selectedSectionIdx: [], useEmbeddings: false };
      }
      state.selectedDocuments[documentId].selectedSectionIdx = sectionIdx;
      // Recalculate section characters when sections are selected/deselected
      state.sectionCharacters = calculateSectionCharacters(
        state.documents,
        state.selectedDocuments,
        state.maxCharsPerSection,
      );
    },
    setUseEmbeddingsForDocument: (
      state,
      action: PayloadAction<{ documentId: string; useEmbeddings: boolean }>,
    ) => {
      const { documentId, useEmbeddings } = action.payload;
      if (!state.selectedDocuments[documentId]) {
        state.selectedDocuments[documentId] = { selectedSectionIdx: [], useEmbeddings };
      } else {
        state.selectedDocuments[documentId].useEmbeddings = useEmbeddings;
      }
    },

    // Add max chars per section action
    setMaxCharsPerSection: (state, action: PayloadAction<number | null>) => {
      state.maxCharsPerSection = action.payload;
      // Recalculate section characters when max chars changes
      state.sectionCharacters = calculateSectionCharacters(
        state.documents,
        state.selectedDocuments,
        action.payload,
      );
    },

    setPrompt: (state, action: PayloadAction<string>) => {
      state.prompt = action.payload;
    },
    setModel: (state, action: PayloadAction<AIModel | null>) => {
      state.model = action.payload;
    },
    setAggregationEnabled: (state, action: PayloadAction<boolean>) => {
      if (action.payload) {
        state.aggregationOptions = {
          aggregatePrompt: '',
          tokensPerPartial: null,
          includePreviousOutput: false,
        };
      } else {
        state.aggregationOptions = null;
      }
    },
    setAggregationOptions: (state, action: PayloadAction<Partial<AggregationOptions>>) => {
      if (state.aggregationOptions) {
        state.aggregationOptions = { ...state.aggregationOptions, ...action.payload };
      }
    },

    // Loading states
    setLoadingDocuments: (state, action: PayloadAction<boolean>) => {
      state.isLoadingDocuments = action.payload;
    },
    setLoadingSections: (state, action: PayloadAction<boolean>) => {
      state.isLoadingSections = action.payload;
    },

    // Summary generation actions
    setSummaryLoading: (state) => {
      state.summaryStatus = SummaryUpdateStatus.Loading;
      state.summaryError = null;
    },
    setSummaryComplete: (
      state,
      action: PayloadAction<{ summary: string; metadata: SummaryMetadata }>,
    ) => {
      state.summaryStatus = SummaryUpdateStatus.Complete;
      state.summaryText = action.payload.summary;
      state.summaryMetadata = action.payload.metadata;
      state.summaryError = null;
      state.summaryGeneratedAt = new Date();
    },
    setSummaryError: (state, action: PayloadAction<string>) => {
      state.summaryStatus = SummaryUpdateStatus.Error;
      state.summaryError = action.payload;
      state.summaryText = null;
      state.summaryMetadata = null;
    },
    reset: (state) => {
      state.companyId = null;
      state.companyName = null;
      state.documents = [];
      state.selectedDocuments = {};
      state.sectionCharacters = 0;
      state.maxCharsPerSection = null;
      state.prompt = '';
      state.summaryText = null;
      state.summaryMetadata = null;
      state.summaryStatus = null;
      state.summaryError = null;
      state.summaryGeneratedAt = null;
      state.isLoadingDocuments = false;
      state.isLoadingSections = false;
      state.aggregationOptions = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(setCompanyAsync.pending, (state) => {
        summaryTestSlice.caseReducers.reset(state);
        state.isLoadingDocuments = true;
      })
      .addCase(setCompanyAsync.fulfilled, (state, action) => {
        state.companyId = action.payload.companyId;
        state.companyName = action.payload.companyName;
        state.documents = action.payload.documents;
        state.isLoadingDocuments = false;
      })
      .addCase(setCompanyAsync.rejected, (state) => {
        state.isLoadingDocuments = false;
      })

      .addCase(setSelectedDocumentIdsAsync.pending, (state) => {
        state.isLoadingSections = true;
      })
      .addCase(setSelectedDocumentIdsAsync.fulfilled, (state, action) => {
        const { documentIds, sectionIdxMapToKeep, newDocumentSections } = action.payload;

        // Update documents with new sections
        newDocumentSections.forEach((docSection) => {
          const documentIndex = state.documents.findIndex(
            (doc) => doc.id === docSection.documentId,
          );
          if (documentIndex !== -1) {
            state.documents[documentIndex].sections = docSection.sections;
            state.documents[documentIndex].hasEmbeddings = docSection.hasEmbeddings;
          }
        });

        // Update selected documents map
        documentIds.forEach((docId) => {
          const document = state.documents.find((doc) => doc.id === docId);
          if (document) {
            // If this document is not already in selectedDocuments, default to all sections selected
            if (!state.selectedDocuments[docId]) {
              state.selectedDocuments[docId] = {
                selectedSectionIdx: document.sections.map((_, idx) => idx),
                useEmbeddings: false,
              };
            } else if (sectionIdxMapToKeep[docId]) {
              state.selectedDocuments[docId].selectedSectionIdx = sectionIdxMapToKeep[docId];
            }
          }
        });

        // Remove documents that are no longer selected
        Object.keys(state.selectedDocuments).forEach((docId) => {
          if (!documentIds.includes(docId)) {
            delete state.selectedDocuments[docId];
          }
        });

        state.sectionCharacters = calculateSectionCharacters(
          state.documents,
          state.selectedDocuments,
          state.maxCharsPerSection,
        );
        state.isLoadingSections = false;
      })
      .addCase(setSelectedDocumentIdsAsync.rejected, (state) => {
        state.isLoadingSections = false;
      });
  },
});

export const {
  setSummaryType,
  setPromptName,
  setSelectedSectionsForDocument,
  setUseEmbeddingsForDocument,
  setMaxCharsPerSection,
  setPrompt,
  setModel,
  setAggregationEnabled,
  setAggregationOptions,
  setLoadingDocuments,
  setLoadingSections,
  setSummaryLoading,
  setSummaryComplete,
  setSummaryError,
  reset,
} = summaryTestSlice.actions;

export default summaryTestSlice.reducer;
